import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import { FileText, Send, Eye, CheckCircle, Clock, XCircle, ExternalLink, Copy, Plus, Filter, Search, Trash2, User } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const statusConfig = {
  draft: { label: 'Draft', icon: FileText, color: 'text-gray-400', bg: 'bg-gray-500/20' },
  sent: { label: 'Sent Pending', icon: Send, color: 'text-blue-400', bg: 'bg-blue-500/20' },
  signed: { label: 'Signed', icon: CheckCircle, color: 'text-green-400', bg: 'bg-green-500/20' },
  expired: { label: 'Expired', icon: Clock, color: 'text-red-400', bg: 'bg-red-500/20' },
  declined: { label: 'Declined', icon: XCircle, color: 'text-red-400', bg: 'bg-red-500/20' }
};

const ProposalsList = () => {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const { user } = useAuth();
  const [proposals, setProposals] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [userFilter, setUserFilter] = useState('mine'); // Default to "My Proposals"
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchProposals();
    fetchUsers();
  }, []);

  const fetchProposals = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/proposals`, { credentials: 'include' });
      if (response.ok) {
        setProposals(await response.json());
      }
    } catch (error) {
      console.error('Failed to fetch proposals:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/users`, { credentials: 'include' });
      if (response.ok) {
        setUsers(await response.json());
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const copyLink = (proposal) => {
    const url = `${window.location.origin}/p/${proposal.access_token}`;
    navigator.clipboard.writeText(url);
    alert('Link copied to clipboard!');
  };

  const deleteProposal = async (proposalId, proposalStatus) => {
    // Only admin can delete signed proposals
    if (proposalStatus === 'signed' && user?.role !== 'admin') {
      alert('Only administrators can delete signed proposals');
      return;
    }
    
    const confirmMsg = proposalStatus === 'signed' 
      ? 'Are you sure you want to delete this SIGNED proposal? This action cannot be undone.'
      : 'Are you sure you want to delete this proposal?';
    
    if (!window.confirm(confirmMsg)) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/proposals/${proposalId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (response.ok) {
        fetchProposals();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to delete proposal');
      }
    } catch (error) {
      console.error('Delete error:', error);
    }
  };

  // Get display status (merge viewed into sent for filtering)
  const getDisplayStatus = (status) => {
    if (status === 'viewed') return 'sent';
    return status;
  };

  const filteredProposals = proposals.filter(p => {
    const displayStatus = getDisplayStatus(p.status);
    if (statusFilter !== 'all' && displayStatus !== statusFilter) return false;
    // Handle "mine" filter for current user - use account_manager_id with fallback to created_by_id
    const proposalOwnerId = p.account_manager_id || p.created_by_id;
    if (userFilter === 'mine' && proposalOwnerId !== user?.user_id) return false;
    if (userFilter !== 'all' && userFilter !== 'mine' && proposalOwnerId !== userFilter) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const companyName = p.client?.company_name?.toLowerCase() || '';
      const title = p.title?.toLowerCase() || '';
      return companyName.includes(query) || title.includes(query);
    }
    return true;
  });

  // Calculate stats for filtered user (or all users)
  const getStats = () => {
    let filteredByUser = proposals;
    if (userFilter === 'mine') {
      // Use account_manager_id with fallback to created_by_id
      filteredByUser = proposals.filter(p => (p.account_manager_id || p.created_by_id) === user?.user_id);
    } else if (userFilter !== 'all') {
      filteredByUser = proposals.filter(p => (p.account_manager_id || p.created_by_id) === userFilter);
    }
    
    return {
      draft: filteredByUser.filter(p => p.status === 'draft').length,
      sent: filteredByUser.filter(p => p.status === 'sent' || p.status === 'viewed').length,
      signed: filteredByUser.filter(p => p.status === 'signed').length,
      expired: filteredByUser.filter(p => p.status === 'expired').length,
      declined: filteredByUser.filter(p => p.status === 'declined').length
    };
  };

  const stats = getStats();

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return `£${(amount || 0).toLocaleString()}`;
  };

  if (loading) {
    return (
      <AppLayout title="Proposals">
        <div className="flex items-center justify-center h-64">
          <div className="text-[#A2FF00] text-xl">Loading...</div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout title="Proposals">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} style={{ fontFamily: 'Unbounded, sans-serif' }}>
              Proposals
            </h1>
            <p className={`mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Manage and track your membership proposals</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => navigate('/member-tracker')}
              className={`flex items-center gap-2 px-4 py-2 ${isDark ? 'bg-[#A2FF00] hover:bg-[#8EFF00]' : 'bg-green-500 hover:bg-green-600'} text-black font-bold rounded-sm transition-colors`}
            >
              <Plus className="w-4 h-4" />
              New Proposal
            </button>
            <button
              onClick={() => navigate('/proposals/bulk-send')}
              className={`flex items-center gap-2 px-4 py-2 ${isDark ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'} text-white font-bold rounded-sm transition-colors`}
            >
              <Send className="w-4 h-4" />
              Bulk Send
            </button>
            <button
              onClick={() => navigate('/proposals/templates')}
              className={`px-4 py-2 ${isDark ? 'bg-[#111] border-white/20 text-white hover:border-[#A2FF00]' : 'bg-white border-gray-300 text-gray-700 hover:border-green-500'} border rounded-sm transition-colors`}
            >
              Templates
            </button>
            <button
              onClick={() => navigate('/admin/products')}
              className={`px-4 py-2 ${isDark ? 'bg-[#111] border-white/20 text-white hover:border-[#A2FF00]' : 'bg-white border-gray-300 text-gray-700 hover:border-green-500'} border rounded-sm transition-colors`}
            >
              Products
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by company name..."
              className={`w-full ${isDark ? 'bg-[#111] border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'} border rounded-sm pl-10 pr-4 py-2 focus:border-[#A2FF00] outline-none`}
            />
          </div>
          <div className="flex items-center gap-2">
            <User className="w-5 h-5 text-gray-500" />
            <select
              value={userFilter}
              onChange={(e) => setUserFilter(e.target.value)}
              className={`${isDark ? 'bg-[#111] border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'} border rounded-sm px-4 py-2 focus:border-[#A2FF00] outline-none`}
            >
              <option value="mine">My Proposals</option>
              <option value="all">All Proposals</option>
              {users.map(u => (
                <option key={u.user_id} value={u.user_id}>{u.name}</option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-gray-500" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className={`${isDark ? 'bg-[#111] border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'} border rounded-sm px-4 py-2 focus:border-[#A2FF00] outline-none`}
            >
              <option value="all">All Status</option>
              <option value="draft">Draft</option>
              <option value="sent">Sent Pending</option>
              <option value="signed">Signed</option>
              <option value="expired">Expired</option>
              <option value="declined">Declined</option>
            </select>
          </div>
        </div>

        {/* Stats Summary - Updated without Viewed */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {Object.entries(statusConfig).map(([status, config]) => {
            const count = stats[status] || 0;
            const Icon = config.icon;
            return (
              <button
                key={status}
                onClick={() => setStatusFilter(statusFilter === status ? 'all' : status)}
                className={`p-4 rounded-sm border transition-colors ${
                  statusFilter === status
                    ? `${isDark ? 'bg-[#A2FF00]/10 border-[#A2FF00]' : 'bg-green-50 border-green-500'}`
                    : `${isDark ? 'bg-[#111] border-white/10 hover:border-white/30' : 'bg-white border-gray-200 hover:border-gray-400'}`
                }`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <Icon className={`w-4 h-4 ${config.color}`} />
                  <span className={`text-xs font-medium ${config.color}`}>{config.label}</span>
                </div>
                <div className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{count}</div>
              </button>
            );
          })}
        </div>

        {/* Proposals List */}
        {filteredProposals.length === 0 ? (
          <div className={`text-center py-12 ${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-200'} border rounded-sm`}>
            <FileText className="w-12 h-12 text-gray-600 mx-auto mb-4" />
            <h3 className={`text-lg font-medium ${isDark ? 'text-white' : 'text-gray-900'} mb-2`}>No proposals found</h3>
            <p className={`mb-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              {searchQuery || statusFilter !== 'all' || userFilter !== 'all'
                ? 'Try adjusting your filters'
                : 'Create a proposal from a card to get started'}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredProposals.map((proposal) => {
              const displayStatus = getDisplayStatus(proposal.status);
              const status = statusConfig[displayStatus] || statusConfig.draft;
              const StatusIcon = status.icon;
              const isViewed = proposal.status === 'viewed';
              // Admin can delete any proposal, others can delete non-signed proposals
              const isAdmin = user?.role === 'admin';
              const canDelete = proposal.status === 'draft' || 
                               proposal.status !== 'signed' ||
                               (proposal.status === 'signed' && isAdmin);
              
              return (
                <div
                  key={proposal.proposal_id}
                  onClick={() => navigate(`/proposals/${proposal.proposal_id}`)}
                  className={`${isDark ? 'bg-[#111] border-white/10 hover:border-white/30' : 'bg-white border-gray-200 hover:border-gray-400'} border rounded-sm p-4 transition-colors cursor-pointer`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        <FileText className={`w-5 h-5 ${isDark ? 'text-[#A2FF00]' : 'text-green-600'} flex-shrink-0`} />
                        <h3 className={`${isDark ? 'text-white' : 'text-gray-900'} font-medium truncate hover:underline`}>
                          {proposal.client?.company_name || 'Unnamed'}
                        </h3>
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${status.bg} ${status.color} flex items-center gap-1`}>
                          <StatusIcon className="w-3 h-3" />
                          {status.label}
                        </span>
                        {isViewed && (
                          <span className="px-2 py-0.5 rounded text-xs font-medium bg-yellow-500/20 text-yellow-400 flex items-center gap-1">
                            <Eye className="w-3 h-3" />
                            Viewed
                          </span>
                        )}
                      </div>
                      
                      <div className="flex flex-wrap items-center gap-4 text-sm">
                        <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Created: {formatDate(proposal.created_at)}</span>
                        <span className={`${isDark ? 'text-[#A2FF00]' : 'text-green-600'} font-medium`}>
                          {formatCurrency(proposal.pricing?.total_gbp)}
                        </span>
                        {proposal.tracking?.sent_at && (
                          <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Sent: {formatDate(proposal.tracking.sent_at)}</span>
                        )}
                        {proposal.tracking?.first_viewed_at && (
                          <span className="text-yellow-400">
                            Viewed: {formatDate(proposal.tracking.first_viewed_at)}
                          </span>
                        )}
                        {proposal.signature?.signed_at && (
                          <span className="text-green-400">
                            Signed: {formatDate(proposal.signature.signed_at)}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                      {proposal.status !== 'signed' ? (
                        <>
                          <button
                            onClick={() => navigate(`/proposals/${proposal.proposal_id}/edit`)}
                            className={`px-3 py-1.5 ${isDark ? 'bg-[#A2FF00] hover:bg-[#8EFF00]' : 'bg-green-500 hover:bg-green-600'} text-black text-sm font-bold rounded-sm transition-colors`}
                          >
                            Edit
                          </button>
                          {proposal.status !== 'draft' && (
                            <>
                              <button
                                onClick={() => copyLink(proposal)}
                                className={`p-1.5 ${isDark ? 'text-gray-400 hover:text-[#A2FF00] hover:bg-[#A2FF00]/10' : 'text-gray-500 hover:text-green-600 hover:bg-green-50'} rounded-sm transition-colors`}
                                title="Copy Link"
                              >
                                <Copy className="w-4 h-4" />
                              </button>
                              <a
                                href={`/p/${proposal.access_token}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`p-1.5 ${isDark ? 'text-gray-400 hover:text-[#A2FF00] hover:bg-[#A2FF00]/10' : 'text-gray-500 hover:text-green-600 hover:bg-green-50'} rounded-sm transition-colors`}
                                title="Open Proposal"
                              >
                                <ExternalLink className="w-4 h-4" />
                              </a>
                            </>
                          )}
                          {canDelete && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteProposal(proposal.proposal_id, proposal.status);
                              }}
                              className="p-1.5 text-red-400 hover:bg-red-500/20 rounded-sm transition-colors"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => navigate(`/proposals/${proposal.proposal_id}`)}
                            className={`px-3 py-1.5 ${isDark ? 'bg-[#111] border-white/20 text-white hover:border-[#A2FF00]' : 'bg-white border-gray-300 text-gray-700 hover:border-green-500'} border text-sm rounded-sm transition-colors`}
                          >
                            View
                          </button>
                          <button
                            onClick={() => copyLink(proposal)}
                            className={`p-1.5 ${isDark ? 'text-gray-400 hover:text-[#A2FF00] hover:bg-[#A2FF00]/10' : 'text-gray-500 hover:text-green-600 hover:bg-green-50'} rounded-sm transition-colors`}
                            title="Copy Link"
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                          <a
                            href={`/p/${proposal.access_token}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`p-1.5 ${isDark ? 'text-gray-400 hover:text-[#A2FF00] hover:bg-[#A2FF00]/10' : 'text-gray-500 hover:text-green-600 hover:bg-green-50'} rounded-sm transition-colors`}
                            title="Open Proposal"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </a>
                          {canDelete && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteProposal(proposal.proposal_id, proposal.status);
                              }}
                              className="p-1.5 text-red-400 hover:bg-red-500/20 rounded-sm transition-colors"
                              title="Delete (Admin only)"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </AppLayout>
  );
};

export default ProposalsList;
